import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import { RouteMeta } from "@/lib/seo";
import { PageFallback } from "@/components/layout/PageFallback";

const loaders = [];
const page = (fn) => { loaders.push(fn); return lazy(fn); };
const prefetchAll = () => loaders.forEach((fn) => fn().catch(() => {}));

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AppShell from "@/components/layout/AppShell";

const Register = page(() => import("@/pages/Register"));
const ForgotPassword = page(() => import("@/pages/ForgotPassword"));
const ResetPassword = page(() => import("@/pages/ResetPassword"));
const LogoPreview = page(() => import("@/pages/LogoPreview"));
const Dashboard = page(() => import("@/pages/Dashboard"));
const Contacts = page(() => import("@/pages/Contacts"));
const Lists = page(() => import("@/pages/Lists"));
const Campaigns = page(() => import("@/pages/Campaigns"));
const CampaignAnalytics = page(() => import("@/pages/CampaignAnalytics"));
const CampaignBuilder = page(() => import("@/pages/CampaignBuilder"));
const Templates = page(() => import("@/pages/Templates"));
const Verification = page(() => import("@/pages/Verification"));
const AITools = page(() => import("@/pages/AITools"));
const Smtp = page(() => import("@/pages/Smtp"));
const SenderProfiles = page(() => import("@/pages/SenderProfiles"));
const Domains = page(() => import("@/pages/Domains"));
const EmailDatabase = page(() => import("@/pages/EmailDatabase"));
const DnsInspector = page(() => import("@/pages/DnsInspector"));
const IpMonitor = page(() => import("@/pages/IpMonitor"));
const Postmaster = page(() => import("@/pages/Postmaster"));
const Team = page(() => import("@/pages/Team"));
const Activity = page(() => import("@/pages/Activity"));
const AcceptInvite = page(() => import("@/pages/AcceptInvite"));
const WhiteLabel = page(() => import("@/pages/WhiteLabel"));
const Automations = page(() => import("@/pages/Automations"));
const AutomationEditor = page(() => import("@/pages/Automations").then((m) => ({ default: m.AutomationEditor })));
const Segments = page(() => import("@/pages/Segments"));
const Suppressions = page(() => import("@/pages/Suppressions"));
const Bounces = page(() => import("@/pages/Bounces"));
const Preferences = page(() => import("@/pages/Preferences"));
const Imports = page(() => import("@/pages/Imports"));
const Deliverability = page(() => import("@/pages/Deliverability"));
const Analytics = page(() => import("@/pages/Analytics"));
const Settings = page(() => import("@/pages/Settings"));
const Profile = page(() => import("@/pages/Profile"));
const AdminUsers = page(() => import("@/pages/AdminUsers"));
const UdbAnalysis = page(() => import("@/pages/UdbAnalysis"));
const ContactProfile = page(() => import("@/pages/ContactProfile"));
const SmartAudiences = page(() => import("@/pages/SmartAudiences"));

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-sm font-mono text-slate-500">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function SuperAdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_super_admin) return <Navigate to="/app" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function App() {
  useEffect(() => {
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
    idle(prefetchAll);
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <RouteMeta />
          <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/accept-invite/:token" element={<AcceptInvite />} />
            <Route path="/logo-preview" element={<LogoPreview />} />
            <Route path="/preferences/:tracking_id" element={<Preferences />} />
            <Route path="/app/campaigns/:id" element={<Protected><CampaignBuilder /></Protected>} />
            <Route path="/app/campaigns/:id/analytics" element={<Protected><CampaignAnalytics /></Protected>} />
            <Route path="/app/automations/:id" element={<Protected><AutomationEditor /></Protected>} />
            <Route path="/app" element={<Protected><AppShell /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="contacts" element={<Contacts />} />
              <Route path="contacts/import" element={<Imports />} />
              <Route path="contacts/:id" element={<ContactProfile />} />
              <Route path="audiences" element={<SmartAudiences />} />
              <Route path="lists" element={<Lists />} />
              <Route path="segments" element={<Segments />} />
              <Route path="campaigns" element={<Campaigns />} />
              <Route path="templates" element={<Templates />} />
              <Route path="templates/:id/edit" element={<CampaignBuilder />} />
              <Route path="verification" element={<Verification />} />
              <Route path="email-database" element={<EmailDatabase />} />
              <Route path="ai-tools" element={<AITools />} />
              <Route path="automations" element={<Automations />} />
              <Route path="suppressions" element={<Suppressions />} />
              <Route path="bounces" element={<Bounces />} />
              <Route path="deliverability" element={<Deliverability />} />
              <Route path="smtp" element={<Smtp />} />
              <Route path="sender-profiles" element={<SenderProfiles />} />
              <Route path="domains" element={<Domains />} />
              <Route path="dns-inspector" element={<DnsInspector />} />
              <Route path="ip-monitor" element={<IpMonitor />} />
              <Route path="postmaster" element={<Postmaster />} />
              <Route path="whitelabel" element={<WhiteLabel />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="settings" element={<Settings />} />
              <Route path="team" element={<Team />} />
              <Route path="activity" element={<Activity />} />
              <Route path="profile" element={<Profile />} />
              <Route path="admin/users" element={<SuperAdminOnly><AdminUsers /></SuperAdminOnly>} />
              <Route path="admin/udb-analysis" element={<SuperAdminOnly><UdbAnalysis /></SuperAdminOnly>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          <Toaster />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
